import { useTranslation } from "react-i18next";
import { numToCurrency } from "../../../utils/currencyHelper";
import { useSelector } from "react-redux";

/**
 * A single printable ticket page (optionally with a tear-off "merchant" stub).
 *
 * Extracted from the previous inline JSX, which duplicated this markup across
 * two near-identical branches (`orderQuantity === 1` vs `> 1`). The only real
 * difference between those branches was whether the cinema name was shown as a
 * header, so that is now a single `showCinemaHeader` prop and the markup lives
 * in one place.
 *
 * `breakAfter` controls the page break. Previously every ticket — including the
 * last one — carried `print:break-after-page`, which makes browsers emit a
 * trailing blank page after the final ticket. That wastes a slip on every sale
 * on thermal/roll printers. The page break is now applied only *between*
 * tickets (see <Ticket/>), so the last page never forces an extra page.
 */
const TicketPage = ({
  ticket,
  showCinemaHeader,
  breakAfter,
  currentOrderId,
  methodsList,
  userData,
  cinemaSettings,
  t,
}) => {
  const priceLabel = ticket.totalPackagePrice
    ? numToCurrency(ticket.totalPackagePrice, true)
    : numToCurrency(ticket.price, true);

  return (
    <div className={`py-3 ${breakAfter ? "print:break-after-page" : ""}`}>
      <p className="text-center font-bold text-[16px]">
        {showCinemaHeader ? userData.cinema : ""}
      </p>
      <p className="text-center text-[16px] font-bold">{ticket.name}</p>
      <div className="flex justify-between">
        <p>
          {t("labels.date")}: {ticket.showtimeDate}
        </p>
        <p>
          {t("labels.time")}: {ticket.time}
        </p>
      </div>
      <div className="flex items-center justify-between ">
        <p>
          {t("labels.price")}: {priceLabel}
        </p>
        <p className=" text-[16px] px-2 font-bold"> {ticket.screen}</p>
      </div>

      <p className="text-center text-[10px]">
        {" "}
        {`${t("labels.orderId")}: ${currentOrderId}`}
      </p>
      {ticket?.ticketName && (
        <p className="text-center text-[10px]">
          {`Ticket: ${ticket?.ticketName}`}
        </p>
      )}
      <p className="text-center text-[10px]">
        {t("labels.staff")}: {`${userData.firstName}  ${userData.lastName}`}
      </p>
      {cinemaSettings?.seatAllocation && ticket?.seatIdentifier && (
        <p className="text-center text-[10px] font-bold">
          {" "}
          {`${t("labels.seatNumber")}: ${ticket?.seatIdentifier}`}
        </p>
      )}
      <p className="text-center text-[10px]">
        {t("footer.appreciation")} {userData.cinema}
      </p>

      {/* Optional tear-off merchant copy printed on the same page as the ticket. */}
      {cinemaSettings.includeCutOffTicket && (
        <div>
          <div className="border-dashed border-b-2 border-[black] my-4 px-2"></div>
          <p className="  text-center font-bold text-[16px]">
            {userData.cinema}
          </p>
          <p className="text-center text-[16px] font-bold">{ticket.name}</p>
          <div className="flex justify-between">
            <p>
              {t("labels.date")}: {ticket.showtimeDate}
            </p>
            <p>
              {t("labels.time")}: {ticket.time}
            </p>
          </div>
          <p className=" text-[16px] font-bold text-center ">
            {" "}
            {t("receipt_type.header.merchant")}
          </p>
          <p className="text-center text-[10px]">
            {" "}
            {`${t("labels.orderId")}: ${currentOrderId}`}
          </p>
          {ticket?.ticketName && (
            <p className="text-center text-[10px]">
              {`Ticket: ${ticket?.ticketName}`}
            </p>
          )}
          <p className="text-center text-[10px]">
            {t("labels.staff")}: {`${userData.firstName}  ${userData.lastName}`}
          </p>
          {cinemaSettings.includeChannelsOnTicket && methodsList?.length > 0 && (
            <p className="py-2">
              {`${
                methodsList?.length > 1
                  ? t("labels.paymentmethod.multiple")
                  : t("labels.paymentmethod.single")
              }`}
              : {methodsList.toString()}
            </p>
          )}
          {cinemaSettings?.seatAllocation && ticket?.seatIdentifier && (
            <p className="text-center text-[10px] font-bold">
              {" "}
              {`${t("labels.seatNumber")}: ${ticket?.seatIdentifier}`}
            </p>
          )}
          <div className="flex items-center justify-between ">
            <p>
              {t("labels.price")}: {priceLabel}
            </p>
            <p className=" text-[16px] px-2 font-bold"> {ticket.screen}</p>
          </div>
        </div>
      )}
    </div>
  );
};

const Ticket = ({ currentOrderId, methodsList = [] }) => {
  const { userData, cinemaSettings } = useSelector((state) => state.profile);
  const orders = useSelector((state) => state.orders);
  const { t } = useTranslation(["receipt"]);

  // Ticket line-items only — concessions have no showtimeId.
  const ticketItems = orders.orderItems.filter((item) => item.showtimeId);

  // Expand every ticket line-item into one printable page per seat/quantity.
  // Flattening up-front gives us a single, ordered list of pages so we can apply
  // the page break *between* tickets only (every page except the last), which
  // avoids the trailing blank page the previous per-ticket break produced.
  const ticketPages = ticketItems.flatMap((ticket) =>
    Array.from({ length: ticket.orderQuantity }, () => ticket)
  );

  if (ticketPages.length === 0) return null;

  return (
    <div>
      {ticketPages.map((ticket, index) => (
        <TicketPage
          key={index}
          ticket={ticket}
          // Preserves prior behaviour: multi-quantity tickets printed the cinema
          // name as a header while single tickets did not.
          showCinemaHeader={ticket.orderQuantity > 1}
          // Break after every page except the last to prevent a trailing blank page.
          breakAfter={index < ticketPages.length - 1}
          currentOrderId={currentOrderId}
          methodsList={methodsList}
          userData={userData}
          cinemaSettings={cinemaSettings}
          t={t}
        />
      ))}
    </div>
  );
};

export default Ticket;
